import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  ArrowLeft, LayoutDashboard, Eye, LogOut, Users, Newspaper, Calendar,
  BookOpen, Globe, CalendarCheck, ClipboardCheck, Mail, Menu as MenuIcon, X,
  Award, Crown, Home, Settings, Send, UserPlus, FileEdit, Palette, Shield, ShieldCheck,
  Image as ImageIcon, Trash2, Edit3, UserCheck, Tag, FolderOpen, AlertTriangle, LifeBuoy, MessageSquare,
} from 'lucide-react';
import logo from '../../assets/logo.png';
import { useAuth } from '../../lib/AuthContext';
import { api } from '../../lib/api';
import { canAccess } from '../../lib/permissions';

// Each link is annotated with a `perm` key (or 'admin' to mean super-admin
// only). The sidebar filters by the current user's role + permissions.
const NAV_SECTIONS = [
  {
    label: 'General',
    links: [
      { to: '/admin', label: 'Panel', icon: LayoutDashboard, exact: true, testid: 'dashboard', perm: 'any' },
    ],
  },
  {
    label: 'Comunidad',
    links: [
      { to: '/admin/community-users', label: 'Usuarios', icon: UserCheck, testid: 'community-users', perm: 'interpreters' },
      { to: '/admin/interpreters', label: 'Intérpretes', icon: Users, testid: 'interpreters', perm: 'interpreters' },
      { to: '/admin/interpreters/bulk', label: 'Editor masivo', icon: Edit3, testid: 'interpreters-bulk', perm: 'interpreters' },
      { to: '/admin/catalogos', label: 'Catálogos', icon: Tag, testid: 'catalogs', perm: 'interpreters' },
      { to: '/admin/registrations', label: 'Registros nuevos', icon: UserPlus, testid: 'registrations', badge: 'registrations', perm: 'registrations' },
      { to: '/admin/dues', label: 'Cuotas', icon: CalendarCheck, testid: 'dues', perm: 'dues' },
      { to: '/admin/profile-edits', label: 'Aprobaciones', icon: ClipboardCheck, testid: 'profile-edits', badge: 'pending', perm: 'profile_edits' },
      { to: '/admin/board', label: 'Consejo Directivo', icon: Crown, testid: 'board', perm: 'board' },
      { to: '/admin/past-presidents', label: 'Expresidentes', icon: Award, testid: 'past-presidents', perm: 'board' },
    ],
  },
  {
    label: 'Contenido',
    links: [
      { to: '/admin/news', label: 'Noticias', icon: Newspaper, testid: 'news', perm: 'news' },
      { to: '/admin/events', label: 'Eventos', icon: Calendar, testid: 'events', perm: 'events' },
      { to: '/admin/periodicals', label: 'Periodicals', icon: BookOpen, testid: 'periodicals', perm: 'periodicals' },
      { to: '/admin/photo-galleries', label: 'Galerías de fotos', icon: ImageIcon, testid: 'photo-galleries', perm: 'galleries' },
      { to: '/admin/foro', label: 'Foro', icon: MessageSquare, testid: 'forum', perm: 'content' },
      { to: '/admin/newsletter', label: 'Newsletter', icon: Send, testid: 'newsletter', perm: 'newsletter' },
    ],
  },
  {
    label: 'Sitio',
    links: [
      { to: '/admin/pages', label: 'Editor de páginas', icon: FileEdit, testid: 'pages', perm: 'pages' },
      { to: '/admin/media', label: 'Archivos', icon: FolderOpen, testid: 'media', perm: 'media' },
      { to: '/admin/theme', label: 'Tema visual', icon: Palette, testid: 'theme', perm: 'pages' },
      { to: '/admin/pages/home/visual', label: 'Editar página Home', icon: Home, testid: 'home-layout', perm: 'pages' },
      { to: '/admin/translations', label: 'Traducciones', icon: Globe, testid: 'translations', perm: 'translations' },
      { to: '/admin/menus', label: 'Menús', icon: Eye, testid: 'menus', perm: 'pages' },
      { to: '/admin/contact', label: 'Mensajes', icon: Mail, testid: 'contact', badge: 'contact', perm: 'contact' },
      { to: '/admin/settings', label: 'Configuración', icon: Settings, testid: 'settings', perm: 'settings' },
    ],
  },
  {
    label: 'Administración',
    links: [
      { to: '/admin/papelera', label: 'Papelera', icon: Trash2, testid: 'trash', perm: 'content' },
      { to: '/admin/support', label: 'Soporte', icon: LifeBuoy, testid: 'support', badge: 'support', perm: 'admin' },
      { to: '/admin/users', label: 'Usuarios admin', icon: Shield, testid: 'users', perm: 'admin' },
      { to: '/admin/roles', label: 'Roles', icon: ShieldCheck, testid: 'roles', perm: 'admin' },
    ],
  },
];

/** True if user can see a given menu item.
 *  - perm 'any'   → all admins/council see it (e.g. dashboard)
 *  - perm 'admin' → super-admin only (role==='admin')
 *  - otherwise    → super-admin OR council member who has that perm
 */
function userCanSee(user, perm) {
  return canAccess(user, perm);
}

export default function AdminLayout() {
  const { pathname } = useLocation();
  const { logout, user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const [unreadContact, setUnreadContact] = useState(0);
  const [pendingRegs, setPendingRegs] = useState(0);
  const [unreadSupport, setUnreadSupport] = useState(0);
  const [stale, setStale] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/admin/profile-edits/pending-count')
      .then(({ data }) => { if (alive) setPendingCount(data.count || 0); })
      .catch(() => {});
    api.get('/admin/stats')
      .then(({ data }) => { if (alive) setUnreadContact(data?.contact?.unread || 0); })
      .catch(() => {});
    api.get('/admin/registration-pending')
      .then(({ data }) => { if (alive) setPendingRegs((data || []).length); })
      .catch(() => {});
    api.get('/admin/pending-approvals/stale')
      .then(({ data }) => { if (alive) setStale(data); })
      .catch(() => {});
    if (user?.role === 'admin') {
      api.get('/admin/support/tickets/unread-count')
        .then(({ data }) => { if (alive) setUnreadSupport(data?.count || 0); })
        .catch(() => {});
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Close sidebar on route change (mobile)
  useEffect(() => { setSidebarOpen(false); }, [pathname]);

  // Some pages need maximum horizontal real estate (e.g. the bulk-edit table).
  // For those, hide the persistent sidebar on lg+. The user can still re-open it
  // by clicking the menu button in the topbar (data-testid=admin-sidebar-open).
  const FULLSCREEN_ROUTES = ['/admin/interpreters/bulk'];
  const isFullscreen = FULLSCREEN_ROUTES.some((r) => pathname.startsWith(r));

  const badges = { pending: pendingCount, contact: unreadContact, registrations: pendingRegs, support: unreadSupport };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed top-0 left-0 z-40 w-64 h-screen bg-charcoal-800 text-white flex flex-col transform transition-transform duration-200 ${
          isFullscreen
            // Fullscreen routes (bulk editor): sidebar behaves like an overlay
            // on every breakpoint — open only when sidebarOpen is true.
            // IMPORTANT: keep `position: fixed` at lg+ (do NOT add lg:sticky),
            // otherwise Tailwind's utility order makes sticky win and the
            // sidebar still reserves 256px of layout space.
            ? `${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
            // Default: persistent sidebar on lg+, hamburger on mobile.
            : `lg:sticky ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`
        }`}
      >
        <div className="flex items-center justify-between gap-3 h-16 px-5 border-b border-charcoal-700 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <img src={logo} alt="CMIC" className="h-8 w-auto brightness-0 invert flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-charcoal-400 font-semibold">CMIC</div>
              <div className="text-xs font-semibold text-white truncate">Administración</div>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 text-charcoal-300 hover:text-white"
            data-testid="admin-sidebar-close"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 min-h-0 px-3 py-4 space-y-6 overflow-y-auto">
          {NAV_SECTIONS.map((section) => {
            const visibleLinks = section.links.filter((link) => userCanSee(user, link.perm));
            if (visibleLinks.length === 0) return null;
            return (
            <div key={section.label}>
              <div className="px-3 mb-2 text-[10px] uppercase tracking-wider text-charcoal-400 font-semibold">{section.label}</div>
              <ul className="space-y-0.5">
                {visibleLinks.map((link) => {
                  const isActive = link.exact ? pathname === link.to : pathname.startsWith(link.to);
                  const badgeValue = link.badge ? (badges[link.badge] || 0) : 0;
                  return (
                    <li key={link.to}>
                      <NavLink
                        to={link.to}
                        end={link.exact}
                        data-testid={`admin-nav-${link.testid}`}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                          isActive
                            ? 'bg-cmic-500 text-white shadow-sm'
                            : 'text-charcoal-300 hover:bg-charcoal-700 hover:text-white'
                        }`}
                      >
                        <link.icon size={16} className="flex-shrink-0" />
                        <span className="flex-1">{link.label}</span>
                        {badgeValue > 0 && (
                          <span
                            data-testid={`admin-nav-${link.testid}-badge`}
                            className={`min-w-[20px] h-[18px] px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${isActive ? 'bg-white text-cmic-600' : 'bg-rose-500 text-white'}`}
                          >
                            {badgeValue}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
            );
          })}
        </nav>

        {/* Sidebar footer: user + actions */}
        <div className="flex-shrink-0 p-3 border-t border-charcoal-700 bg-charcoal-800 space-y-2">
          {user && (
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-charcoal-700/50">
              <div className="w-8 h-8 rounded-full bg-cmic-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {(user.username || '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-white truncate">{user.full_name || user.username}</div>
                <div className="text-[10px] text-charcoal-400 truncate">@{user.username}</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/es"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-site-link"
              className="inline-flex items-center justify-center gap-1.5 text-[11px] font-medium text-charcoal-300 hover:text-white bg-charcoal-700 hover:bg-charcoal-600 rounded-lg px-2 py-2 transition-colors"
            >
              <ArrowLeft size={12} />Sitio
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center justify-center gap-1.5 text-[11px] font-medium text-rose-300 hover:text-white bg-charcoal-700 hover:bg-rose-600 rounded-lg px-2 py-2 transition-colors"
            >
              <LogOut size={12} />Salir
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — always shown on mobile, also shown on desktop when in
            fullscreen mode so the user can re-open the sidebar to navigate. */}
        <header className={`${isFullscreen ? '' : 'lg:hidden'} bg-white border-b border-gray-200 sticky top-0 z-20`}>
          <div className="flex items-center justify-between h-14 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              data-testid="admin-sidebar-open"
              className="p-2 text-charcoal-600 hover:bg-gray-100 rounded-lg inline-flex items-center gap-2"
              title="Abrir menú"
            >
              <MenuIcon size={18} />
              {isFullscreen && <span className="text-xs font-medium hidden sm:inline">Abrir menú</span>}
            </button>
            <span className="text-sm font-semibold text-charcoal-800">CMIC Admin</span>
            <span className="w-9" />
          </div>
        </header>

        <main className={`flex-1 ${isFullscreen ? 'p-4' : 'p-4 sm:p-6 lg:p-8'}`}>
          {stale && stale.total > 0 && (
            <div data-testid="stale-approvals-banner"
              className="mb-5 flex items-start gap-3 bg-amber-50 border border-amber-300 text-amber-900 rounded-xl px-4 py-3">
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5 text-amber-600" />
              <div className="text-sm flex-1">
                <p className="font-semibold">Aprobaciones pendientes de revisar (+{stale.threshold_days} días)</p>
                <p className="text-amber-800">
                  Hay aprobaciones que llevan más de {stale.threshold_days} días sin revisarse:
                  {stale.registrations > 0 && (
                    <> <Link to="/admin/registrations" className="font-semibold underline hover:text-amber-950" data-testid="stale-banner-registrations">{stale.registrations} registro(s) nuevo(s)</Link></>
                  )}
                  {stale.registrations > 0 && stale.profile_edits > 0 && <> y</>}
                  {stale.profile_edits > 0 && (
                    <> <Link to="/admin/profile-edits" className="font-semibold underline hover:text-amber-950" data-testid="stale-banner-edits">{stale.profile_edits} solicitud(es) de edición</Link></>
                  )}.
                </p>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
